import { AxiosError, InternalAxiosRequestConfig } from "axios";

import { refresh } from "./authApi";
import { Credentials } from "./models";
import { notifySessionExpired } from "./session";
import {
  hasValidSession,
  loadCredentials,
  saveCredentials,
  clearCredentials,
  isAccessTokenExpired,
} from "./tokenStorage";

import { apiClient } from "@shared/http/apiClient";

declare module "axios" {
  interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
  }

  interface InternalAxiosRequestConfig {
    skipAuthRefresh?: boolean;
    _retry?: boolean;
  }
}

let refreshInFlight: Promise<Credentials> | null = null;

function getOrStartRefresh(): Promise<Credentials> {
  if (!refreshInFlight) {
    refreshInFlight = refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export function setupAuthInterceptors(): void {
  apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    let credentials = loadCredentials();

    if (
      !config.skipAuthRefresh &&
      credentials &&
      isAccessTokenExpired(credentials) &&
      hasValidSession(credentials)
    ) {
      try {
        credentials = await getOrStartRefresh();
        saveCredentials(credentials);
      } catch {
        // Let the response interceptor deal with the resulting 401.
      }
    }

    if (credentials && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${credentials.token.key}`;
    }

    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig | undefined;

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest.skipAuthRefresh ||
        originalRequest._retry
      ) {
        return Promise.reject(error);
      }

      const credentials = loadCredentials();
      if (!credentials || !hasValidSession(credentials)) {
        clearCredentials();
        notifySessionExpired("Your session has expired. Please sign in again.");
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const refreshed = await getOrStartRefresh();
        saveCredentials(refreshed);
        originalRequest.headers.Authorization = `Bearer ${refreshed.token.key}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        clearCredentials();
        notifySessionExpired("Your session has expired. Please sign in again.");
        return Promise.reject(refreshError);
      }
    }
  );
}
